import { useEffect } from 'react'
import type { Editor } from '@tiptap/react'

/**
 * Shrink editor content wider than the editor so it fits, never enlarging it. A designed email is laid out
 * 600–700px wide; in a narrow pane it would run off the side. Only the screen is scaled — the HTML is not.
 *
 * Re-fits on pane resizes and document changes, at most once a frame: typing fires a transaction per keystroke,
 * and each fit measures layout.
 */
export const useFitToWidth = (editor: Editor | null, enabled: boolean) => {
    useEffect(() => {
        if (!editor || !enabled || typeof ResizeObserver === 'undefined') return
        const surface = editor.view.dom as HTMLElement
        // Measured unscaled and re-scaled in one task, so nothing paints at full size in between.
        const fit = () => {
            surface.style.zoom = ''
            const scale = surface.scrollWidth > surface.clientWidth ? surface.clientWidth / surface.scrollWidth : 1
            surface.style.zoom = scale < 1 ? String(scale) : ''
        }
        let frame = 0
        const scheduleFit = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(fit)
        }
        const fitOnDocChange = ({ transaction }: { transaction: { docChanged: boolean } }) => {
            if (transaction.docChanged) scheduleFit()
        }
        fit()
        editor.on('transaction', fitOnDocChange)
        const observer = new ResizeObserver(scheduleFit)
        if (surface.parentElement) observer.observe(surface.parentElement)
        return () => {
            cancelAnimationFrame(frame)
            editor.off('transaction', fitOnDocChange)
            observer.disconnect()
            surface.style.zoom = ''
        }
    }, [editor, enabled])
}
